import type {
    ContentCatalogEntry,
    ContentRepository
} from "./contentRepository.js";


/**
 * Transitional repository used while legacy static content still coexists
 * with canonical server content.
 *
 * Server-owned content:
 *
 * - grammar_lesson
 * - home_feature
 *
 * These content types MUST NOT read or merge anything from data/.
 *
 * Other historical content types continue to use the transitional overlay
 * until their migration is complete.
 */
function createTransitionalContentRepository(
    server:
        ContentRepository,

    historical:
        ContentRepository
): ContentRepository {
    return {
        source:
            "server",


        /* ------------------------------------------------------------------ */
        /* Catalog                                                            */
        /* ------------------------------------------------------------------ */

        async listCatalog(
            contentType,
            level = null
        ) {
            /*
             * Home editorial content has no static representation by design.
             *
             * If Supabase contains zero published home_feature rows,
             * the result is therefore simply [].
             */
            if (
                isServerOwnedContent(
                    contentType
                )
            ) {
                return server.listCatalog(
                    contentType,
                    level
                );
            }

            const [
                historicalEntries,
                publishedEntries
            ] =
                await Promise.all([
                    historical.listCatalog(
                        contentType,
                        level
                    ),

                    server.listCatalog(
                        contentType,
                        level
                    )
                ]);

            const entries =
                new Map<
                    string,
                    ContentCatalogEntry
                >();

            /*
             * Historical content is inserted first.
             * A published server revision with the same identity wins.
             */
            for (
                const entry
                of historicalEntries
            ) {
                entries.set(
                    entry.contentKey,
                    entry
                );
            }

            for (
                const entry
                of publishedEntries
            ) {
                entries.set(
                    entry.contentKey,
                    entry
                );
            }

            return [
                ...entries.values()
            ];
        },


        /* ------------------------------------------------------------------ */
        /* Document                                                           */
        /* ------------------------------------------------------------------ */

        async loadDocument(
            contentType,
            contentKey,
            levelHint = null
        ) {
            const published =
                await server.loadDocument(
                    contentType,
                    contentKey,
                    levelHint
                );

            /*
             * Server-owned domains never fall back to static files.
             */
            if (
                published
                || isServerOwnedContent(
                    contentType
                )
            ) {
                return published;
            }

            return historical.loadDocument(
                contentType,
                contentKey,
                levelHint
            );
        }
    };
}


/* -------------------------------------------------------------------------- */
/* Server-owned content                                                       */
/* -------------------------------------------------------------------------- */

function isServerOwnedContent(
    contentType:
        Parameters<
            ContentRepository["listCatalog"]
        >[0]
): boolean {
    return (
        contentType
            === "grammar_lesson"
        || contentType
            === "home_feature"
    );
}


/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

export {
    createTransitionalContentRepository
};